import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

/**
 * 验证重定向 URL 是否安全（前端纵深防御）
 * @param {string} url - 目标 URL
 * @returns {boolean} 是否安全
 */
function isSafeRedirectUrl(url) {
  if (!url || typeof url !== 'string') return false;

  // 允许的内部路径前缀
  const allowedPrefixes = [
    '/',          // 首页/牛人榜
    '/traders/',  // 交易员详情
    '/market/',   // 市场详情
    '/favorites/' // 收藏详情
  ];

  // 检查是否包含协议（防止完整 URL）
  if (url.includes('://') || url.startsWith('//')) {
    return false;
  }

  // 检查路径穿越
  if (url.includes('..')) {
    return false;
  }

  // 检查是否以允许的前缀开头
  return allowedPrefixes.some(prefix => url.startsWith(prefix));
}

/**
 * 分享链接重定向页面
 *
 * 功能流程：
 * 1. 解析 URL 中的 share_id
 * 2. 调用 API 获取分享链接信息（记录点击、设置 cookie）
 * 3. 构建目标 URL（带查询参数）
 * 4. 前端路由跳转
 */
export default function ShareRedirect() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [error, setError] = useState(null);
  const hasCalledApi = useRef(false); // 防止重复调用

  useEffect(() => {
    if (!shareId) {
      console.error('[ShareRedirect] shareId is missing');
      navigate('/');
      return;
    }

    // 防止 React StrictMode 导致的重复调用
    if (hasCalledApi.current) {
      return;
    }
    hasCalledApi.current = true;

    // 调用 API 获取分享链接信息
    const url = new URL(`${API_BASE_URL}/api/v1/share/${shareId}/info`);
    url.searchParams.set('lang', lang);
    fetch(url.toString(), {
      method: 'GET',
      credentials: 'include', // 包含 cookie
      headers: {
        'Accept': 'application/json',
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((response) => {
        console.log('[ShareRedirect] API response:', response);

        if (response.code === 0 && response.data) {
          const { target_page, target_params, share_id } = response.data;

          // 【安全验证】前端二次验证目标页面（纵深防御）
          if (!isSafeRedirectUrl(target_page)) {
            console.error('[ShareRedirect] 不安全的目标URL:', target_page);
            setError(t('share.invalidTarget'));
            setTimeout(() => navigate('/'), 3000);
            return;
          }

          // 构建查询参数
          const params = new URLSearchParams();

          // 处理不同分享类型的参数
          if (target_params.selectedDomains && Array.isArray(target_params.selectedDomains)) {
            params.set('domains', target_params.selectedDomains.join(','));
          }
          if (target_params.sortBy) {
            params.set('sortBy', target_params.sortBy);
          }
          if (target_params.sortOrder) {
            params.set('sortOrder', target_params.sortOrder);
          }

          // 添加分享引用ID（用于后续转化追踪）
          params.set('ref', share_id);

          // 构建完整路径
          const targetUrl = `${target_page}?${params.toString()}`;
          console.log('[ShareRedirect] Redirecting to:', targetUrl);

          // 前端路由跳转
          navigate(targetUrl);
        } else {
          // API 返回错误
          console.error('[ShareRedirect] API error:', response.msg || response.message);
          setError(response.msg || response.message || t('share.invalidLink'));

          // 3秒后跳转到首页
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
      })
      .catch((err) => {
        console.error('[ShareRedirect] Request failed:', err);
        setError(t('share.loadFailed'));

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/');
        }, 3000);
      });
  }, [shareId, navigate, lang, t]);

  // 加载中 / 错误状态的 UI
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950">
      <div className="text-center">
        {error ? (
          <>
            <div className="text-red-400 text-lg mb-4">{error}</div>
            <div className="text-slate-400 text-sm">{t('share.redirectingHome')}</div>
          </>
        ) : (
          <>
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-slate-400 text-lg">{t('share.redirecting')}</div>
          </>
        )}
      </div>
    </div>
  );
}
